import ClickableTableCell from 'Components/ClickableTableCell'
import CustomButton from 'Components/CustomButton'
import type { HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { SELECTED_SALES_POINT_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/SalesPoint/Hooks/useSalesPointsWithParams'
import type { SalesPoint } from 'Services/ServiceProviderManagement/Features/SalesPoint/salesPointSlice'
import { SELECTED_SERVICE_PROVIDER_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/ServiceProvider/Hooks/useServiceProvidersWithParams'
import type { ServiceProvider } from 'Services/ServiceProviderManagement/Features/ServiceProvider/serviceProviderSlice'
import { SERVICE_PROVIDER_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { showValueOrHyphen } from 'Util/formatters'
import AddIcon from '@mui/icons-material/Add'
import {
  Box,
  Chip,
  type ChipProps,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

interface ServiceProviderSalesPointsProps {
  salesPoints: SalesPoint[]
  serviceProvider: ServiceProvider
}

export function ServiceProviderSalesPoints({
  salesPoints,
  serviceProvider
}: Readonly<ServiceProviderSalesPointsProps>) {
  const baseUrl = `${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/sales-points`
  const navigate = useNavigate()

  const headCells: HeadCell[] = [
    {
      id: 'sales_point_id',
      numeric: false,
      disablePadding: false,
      label: t`Code`,
      sortable: false
    },
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t`Sales point name`,
      sortable: false
    },
    {
      id: 'activity',
      numeric: false,
      disablePadding: false,
      label: t`Activity`,
      sortable: false
    }
  ]

  const navigateToSalesPointPage = (salesPointId: string) => {
    return navigate(`${baseUrl}/?${SELECTED_SALES_POINT_PARAMETER_KEY}=${salesPointId}`)
  }

  const chipPropsMapping: Record<string, Pick<ChipProps, 'color' | 'label'>> = {
    ACTIVE: { color: 'success', label: t`Active` },
    PASSIVE: { color: 'default', label: t`Passive` }
  }

  function getChipPropsByActivity(status: boolean) {
    return chipPropsMapping[status ? 'ACTIVE' : 'PASSIVE']
  }

  function salesPointActivityMapping(status: boolean) {
    switch (status) {
      case true:
        return 'ACTIVE'
      case false:
        return 'PASSIVE'
    }
  }

  return (
    <Box>
      <Paper sx={{ width: '100%', mb: 2, pt: 2 }}>
        <TableContainer>
          <Table aria-labelledby="tableTitle" size="small">
            <TableHead>
              <TableRow>
                {headCells.map(cell => (
                  <TableCell key={cell.id}>{cell.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {salesPoints.length > 0 ? (
                salesPoints.map(row => {
                  return (
                    <StyledTableRow hover tabIndex={-1} key={row.sales_point_id}>
                      <ClickableTableCell
                        onClick={() => navigateToSalesPointPage(row.sales_point_id)}
                      >
                        {showValueOrHyphen(row.sales_point_id)}
                      </ClickableTableCell>
                      <ClickableTableCell
                        onClick={() => navigateToSalesPointPage(row.sales_point_id)}
                      >
                        {showValueOrHyphen(row.name)}
                      </ClickableTableCell>
                      <NoWrapTableCell title={salesPointActivityMapping(row.active)}>
                        <Chip size="small" {...getChipPropsByActivity(row.active)} />
                      </NoWrapTableCell>
                    </StyledTableRow>
                  )
                })
              ) : (
                <StyledTableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2">{t`Service provider has no sales points.`}</Typography>
                  </TableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <CustomButton
        variant="text"
        size="small"
        startIcon={<AddIcon />}
        sx={{ ml: 2 }}
        onClick={() =>
          navigate(
            `${baseUrl}/create?${SELECTED_SERVICE_PROVIDER_PARAMETER_KEY}=${serviceProvider.service_provider_id}`
          )
        }
      >{t`Add sales point`}</CustomButton>
    </Box>
  )
}
